/**
 * TWO TRACKS THROUGH ONE JOB, AND WHAT IS LEFT ON EACH.
 *
 * Anthony: "Work on wizard track for Service Advisor. Anyone with an admin
 * role, supervisor, Owner role should also be able to see both tracks as
 * long as there are things remaining to be done in both."
 *
 * A job is worked by two people who never do the same thing. The advisor
 * finds out what the car is, prices it, gets the yes, books it and takes
 * the money. The technician stands at the car. In a shop with a desk those
 * are two people; in most shops on this system they are the same person
 * wearing two hats, which is precisely why the two lists have to exist
 * separately even when one human walks both.
 *
 * ─── WHY THIS FILE IS PURE ─────────────────────────────────────────────
 *
 * Every rule about what is finished lives here, in functions that take a
 * plain object and return an answer. No database, no DOM. That means the
 * entry cards on the job screen, the walk itself, and a test can all ask
 * the same question and cannot drift into three different opinions about
 * whether a job is ready to send.
 *
 * ─── THE RULES, STATED ONCE ────────────────────────────────────────────
 *
 *   1. A step is DONE when the job's own record says so, never when
 *      somebody clicked past it. Put the phone down at lunch and come back
 *      to the same place.
 *   2. A track is OFFERED only while it has something left. A card that
 *      opens a walk with nothing in it is a card that teaches people to
 *      ignore cards.
 *   3. The order is the order the work happens in, so "what is next" is
 *      the first unfinished step and never a menu.
 *   4. The two tracks OVERLAP at the money. Billing and payment appear on
 *      both, because Anthony: "either advisor or tech basically should"
 *      be able to do them, and whoever gets there first should.
 *   5. A step can be BLOCKED rather than merely unfinished. Sending a
 *      quote with no price is not a thing to be nudged about, it is a
 *      thing that cannot happen yet, and the walk says which.
 */

import { words } from "./trade.ts";

export type TrackKey = "advisor" | "tech";

/**
 * Everything either track needs to know, read from the job once. Plain
 * booleans on purpose: a rule that has to interpret a timestamp is a rule
 * two screens will interpret differently.
 */
export type JobFacts = {
  status: string;
  /** The six things a quote needs. See domain/quoteready.ts. */
  hasCustomer: boolean;
  hasPhone: boolean;
  hasPlace: boolean;
  hasCar: boolean;
  hasComplaint: boolean;
  /** Money on the lines, before any discount. */
  priced: number;
  /** The quote actually went to them. quote_expires_at is stamped on send. */
  quoteSent: boolean;
  /** Somebody agreed to a figure: signed, or recorded from a phone call. */
  authorized: boolean;
  /** A date on the job. */
  scheduled: boolean;
  /** Somebody is on it. */
  assigned: boolean;
  /** Work began. */
  started: boolean;
  arrivalShot: boolean;
  releaseShot: boolean;
  /**
   * The technician has written up what they found. It used to be a count
   * of rows in job_tests, back when the walk made them record each test
   * and mark it passed or failed; that step was removed on 2026-09-21 and
   * this is the stamp its replacement leaves on the job's notes.
   */
  found: boolean;
  /** A plate or a VIN. */
  identified: boolean;
  invoiced: boolean;
  paid: boolean;
  /** Warranty or goodwill: no price, no bill, and that is correct. */
  noCharge: boolean;
  /**
   * A garage door shop's free estimate visit: the quote is an offer to
   * come and look, with no price on it by design (Anthony, 2026-09-29;
   * src/domain/visitquote.ts). It counts as priced for the purpose of
   * sending it, and nothing else about the track moves. Omitted is false.
   */
  visitQuote?: boolean;
};

export type Step = {
  key: string;
  title: string;
  /** What the person is being asked to do, in their words. */
  sub: string;
  /** True when the job's record says this is finished. */
  done: (f: JobFacts) => boolean;
  /**
   * Why this step cannot be done yet, or null when it can. A blocked step
   * is still unfinished; it simply cannot be the one you are sent to.
   */
  blocked?: (f: JobFacts) => string | null;
  /**
   * ADVISORY, NOT A GATE.
   *
   * Anthony, 2026-09-09: "service advisor track looks stuck asking for
   * information that's already in the details field."
   *
   * "Who and what" is a completeness check on a record, not a thing you
   * do. Left as an ordinary step it is a wall: it is first, it is rarely
   * perfectly complete on a real job, and "what is next" always means the
   * first unfinished step -- so an advisor who priced the job, sent it and
   * got the yes still landed back on step one every time they opened the
   * walk, being asked for a mobile number that is on the card behind it.
   *
   * A soft step is still unfinished and still counted. It simply stops
   * being where you are SENT once there is later progress, because there
   * plainly is no longer a question about whether you can start.
   */
  soft?: boolean;
};

const closed = (f: JobFacts) => f.status === "cancelled";

/**
 * The work is over. Used by the steps that can only be proved by a photo or
 * a note, because a job the technician marked finished IS finished, and a
 * step that can never go green is a step that teaches people the list lies.
 * A missing photo is a nag on the job card, not an open step forever.
 */
const past = (f: JobFacts) => ["completed", "invoiced", "paid"].includes(f.status);

/* ── the advisor's track ──────────────────────────────────────────── */

export const ADVISOR_STEPS: Step[] = [
  {
    key: "who",
    title: "Who and what",
    sub: "Their name, a number, where the car is, what it is and what it is doing.",
    done: (f) => f.hasCustomer && f.hasPhone && f.hasPlace && f.hasCar && f.hasComplaint,
    soft: true,
  },
  {
    key: "price",
    title: "Price it",
    sub: "Lines with money on them. Draft it from the conversation if you want a start.",
    done: (f) => f.noCharge || f.priced > 0 || Boolean(f.visitQuote),
  },
  {
    key: "send",
    title: "Send it to them",
    sub: "The price, and a link to their own page where they can approve it.",
    done: (f) => f.noCharge || f.quoteSent,
    blocked: (f) => (f.noCharge || f.priced > 0 || f.visitQuote) ? null
      : "Nothing is priced yet, so there is no quote to send.",
  },
  {
    key: "yes",
    title: "The yes",
    sub: "Signed on their screen, or told to you on the phone and written down.",
    done: (f) => f.noCharge || f.authorized,
  },
  {
    key: "book",
    title: "Book it in",
    sub: "A day and a time. Nothing is booked before there is a yes.",
    done: (f) => f.scheduled,
    blocked: (f) => (f.noCharge || f.authorized) ? null
      : "No yes on file. A booking before an authorization is a promise the shop cannot keep.",
  },
  {
    key: "who_goes",
    title: "Who is going",
    sub: "Put somebody on it, even if that somebody is you.",
    done: (f) => f.assigned || past(f),
  },
  {
    key: "bill",
    title: "Bill it",
    sub: "The total comes off the lines. Nobody types it.",
    done: (f) => f.noCharge || f.invoiced || f.paid,
    blocked: (f) => f.noCharge
      ? "This one is not being billed."
      : ["completed", "invoiced", "paid"].includes(f.status) ? null
      : "The work is not finished yet.",
  },
  {
    key: "money",
    title: "Take the money",
    sub: "Card, cash, check or transfer, and the job closes itself.",
    done: (f) => f.noCharge || f.paid,
    blocked: (f) => f.noCharge
      ? "This one is not being billed."
      : (f.invoiced || f.paid) ? null : "There is no bill yet.",
  },
];

/* ── the technician's track ───────────────────────────────────────── */

export const TECH_STEPS: Step[] = [
  {
    key: "start",
    title: "Start the job",
    sub: "The yes on file, the miles, and one photo of how it turned up.",
    done: (f) => f.started || f.arrivalShot,
    blocked: (f) => (f.noCharge || f.authorized) ? null
      : "Nobody has said yes yet, so it is not yours to start.",
  },
  {
    key: "inspect",
    title: "The inspection",
    sub: "Walk the car before anything comes apart.",
    // Nothing on the record proves an inspection was walked, so it counts
    // as done once work has started rather than pretending to know.
    done: (f) => f.started || past(f),
  },
  {
    key: "found",
    title: "What you found",
    sub: "What is wrong with it, in your own words, and the photos.",
    done: (f) => f.found || past(f),
  },
  {
    /* Keyed "money" to match the walk's own step key in pages/tech.ts, so a
       card can send somebody straight to it rather than to the start. */
    key: "money",
    title: "Is it bigger than the quote",
    sub: "If it is, you stop and the desk gets a new yes.",
    done: (f) => f.found || past(f),
  },
  {
    key: "repair",
    title: "Do the repair",
    sub: "Keep shooting as you go.",
    done: (f) => f.releaseShot || past(f),
  },
  {
    key: "after",
    title: "Photos after",
    sub: "The finished work, and the old part beside the new one.",
    done: (f) => f.releaseShot || past(f),
  },
  {
    key: "car",
    title: "The car",
    sub: "A plate or a VIN. The job cannot close without one.",
    done: (f) => f.identified || past(f),
  },
  {
    key: "done",
    title: "Mark it finished",
    sub: "Everything the job needs is on it.",
    done: (f) => ["completed", "invoiced", "paid"].includes(f.status),
  },
  {
    key: "bill",
    title: "Bill it",
    sub: "The total comes off the lines.",
    done: (f) => f.noCharge || f.invoiced || f.paid,
    blocked: (f) => f.noCharge
      ? "This one is not being billed."
      : ["completed", "invoiced", "paid"].includes(f.status) ? null
      : "The work is not finished yet.",
  },
  {
    key: "paid",
    title: "Take the money",
    sub: "Card, cash, check or transfer. Then the keys go back.",
    done: (f) => f.noCharge || f.paid,
    blocked: (f) => f.noCharge
      ? "This one is not being billed."
      : (f.invoiced || f.paid) ? null : "There is no bill yet.",
  },
];

/**
 * THE STEPS AS A SHOP IN THIS TRADE READS THEM.
 *
 * Anthony, 2026-09-29, after the Swift training film showed a garage door
 * job card saying "Walk the car before anything comes apart": fix the car
 * wording for door shops. The tech page had already rewritten that line at
 * render (stepWords in src/pages/tech.ts), but the job card's two doors
 * and the advisor walk read these steps through nextOn() and summaryOf(),
 * which never passed a trade, so every shop got the auto sentences. The
 * rewrite lives here now and tech.ts calls it, so there is one copy.
 *
 * A trade with a plate (auto, RV) reads the steps exactly as written above.
 * The rest lose the car: the address instead of "where the car is", their
 * own thing walked instead of the car, no miles where nothing counts them,
 * and no keys handed back where no keys were handed over.
 */
export function reword<S extends { key: string; title: string; sub: string }>(
  s: S, trade?: string | null,
): S {
  const w = words(trade);
  /* The identify step: "a plate or a VIN" describes something a garage
     door can never have. Omitting the trade gives the auto words, so a
     caller that passes none reads exactly as it did.

     ─── THIS USED TO SHORT CIRCUIT ON A DISPLAY STRING ─────────────────

     It read `if (w.identifyTitle === "The car") return base;` as a way of
     saying "this is the auto trade, nothing to substitute". That works
     only for as long as no other trade's job hangs off a car, and it held
     for the door and the lock because theirs are "The door" and "The
     lock".

     Headlight restoration broke it. A headlight job IS about a car, so its
     title is legitimately "The car" too -- and the guard matched, returned
     the base steps untouched, and handed that shop auto's sentence: "A
     plate or a VIN. The job cannot close without one." Their own screen
     said the year, make and model, because isIdentified reads hasPlate and
     got it right. So the manual and the wizard were telling a technician
     to go and find a number that the job did not need and that nobody was
     ever going to write down.

     The substitution is now unconditional. For auto it is a no-op by
     construction: the "car" step's title and sub above are byte identical
     to auto's own identifyTitle and identifySub, so mapping them onto
     themselves changes nothing. A comparison against a word somebody might
     reasonably reuse is not a way to identify a trade. */
  if (s.key === "car") return { ...s, title: w.identifyTitle, sub: w.identifySub };
  if (w.hasPlate) return s;
  if (s.key === "who") {
    return { ...s, sub: "Their name, a number, the address, what it is and what it is doing." };
  }
  if (s.key === "inspect") return { ...s, sub: `Walk the ${w.thing} before anything comes apart.` };
  /* "The yes on file, the miles" promises an odometer box the screen does
     not show for a trade that has none, and a door did not "turn up". */
  if (s.key === "start" && !w.reading) {
    return { ...s, sub: `The yes on file, and one photo of the ${w.thing} before you start.` };
  }
  /* "the old part beside the new one" is a swap. An investigator does not
     swap anything out, and the sentence is about proving the work was
     done -- which for them is the finished thing on its own. */
  if (s.key === "after" && !w.hasEngine) {
    return { ...s, sub: `The finished work, and the old ${w.partLabel} beside the new one if there was one.` };
  }
  if (s.key === "paid" && s.sub.includes("keys go back")) {
    return { ...s, sub: "Card, cash, check or transfer." };
  }
  return s;
}

export const stepsFor = (track: TrackKey, trade?: string | null): Step[] =>
  (track === "advisor" ? ADVISOR_STEPS : TECH_STEPS).map((s) => reword(s, trade));

/** Everything still owed on a track, in the order it should be done. */
export function leftOn(track: TrackKey, f: JobFacts, trade?: string | null): Step[] {
  if (closed(f)) return [];
  return stepsFor(track, trade).filter((s) => !s.done(f));
}

/**
 * The one to send somebody to: the first unfinished step that is not
 * blocked by something earlier. A walk that opens on a step you cannot do
 * is the walk arguing with the shop.
 */
export function nextOn(track: TrackKey, f: JobFacts, trade?: string | null): Step | null {
  const all = stepsFor(track, trade);
  const moved = (s: Step): boolean => {
    /* Has anything AFTER this one been done? Then the walk is not going
       to send somebody back to it. */
    const i = all.indexOf(s);
    return all.slice(i + 1).some((later) => later.done(f));
  };
  const left = leftOn(track, f, trade);
  for (const s of left) {
    if (s.blocked?.(f)) continue;
    if (s.soft && moved(s)) continue;
    return s;
  }
  /* Everything left is soft and overtaken, or blocked. A soft step is
     still a real thing to finish, so offer the first one rather than
     claiming the track is done. */
  for (const s of left) if (!s.blocked?.(f)) return s;
  return null;
}

/**
 * WHERE A WALK OPENS, which is not always where it sends you next.
 *
 * nextOn() is null when everything left is blocked, and the desk walk
 * used to fall back to step one on that null. That is the ordinary state
 * of every booked job waiting on the car: the desk owes Bill it and Take
 * the money, both blocked until the work is finished. So a job priced,
 * agreed and booked opened on Who and what, the one thing the walkthrough
 * promises it never does. Found checking the live desk, 2026-09-24.
 *
 * So a walk with nothing doable opens on the first thing it still owes.
 * That step says Not yet and why, and What is next then carries on.
 */
export function openOn(track: TrackKey, f: JobFacts, trade?: string | null): Step | null {
  return nextOn(track, f, trade) ?? leftOn(track, f, trade)[0] ?? null;
}

/**
 * What the entry card says. A count and the name of the next thing, so
 * somebody can decide whether to open it without opening it.
 */
export function summaryOf(track: TrackKey, f: JobFacts, trade?: string | null): {
  left: number; next: Step | null; blockedBy: string | null;
} {
  const left = leftOn(track, f, trade);
  const next = nextOn(track, f, trade);
  const blockedBy = !next && left.length ? (left[0]!.blocked?.(f) ?? null) : null;
  return { left: left.length, next, blockedBy };
}

/**
 * WHICH TRACKS A PERSON MAY WALK.
 *
 * Anthony: "Anyone with an admin role, supervisor, Owner role should also
 * be able to see both tracks." A technician gets the car; an advisor gets
 * the desk; the people who run the shop get both, because in most shops on
 * this system they are doing both.
 *
 * Viewers get neither: they cannot change anything, so a walk that refuses
 * at every step would be a cruelty rather than a feature.
 */
/**
 * The color each track wears, everywhere it appears. Anthony: "Green is a
 * good background for the technician service track and we can use Blue for
 * the Service Advisor track and gold for an owner or supervisor."
 *
 * Two colors for two kinds of work, and the gold is not a third track --
 * there is no owner's track -- it is what the pair of doors is framed in
 * when both of them belong to the same person.
 */
export const TRACK_COLOUR: Record<TrackKey, string> = {
  advisor: "blue",
  tech: "green",
};

/** Whoever holds both tracks holds the shop. That frame is gold. */
export const holdsBoth = (role: string): boolean => tracksFor(role).length === 2;

export function tracksFor(role: string): TrackKey[] {
  if (role === "technician") return ["tech"];
  if (role === "advisor") return ["advisor"];
  if (role === "admin" || role === "supervisor" || role === "owner") return ["advisor", "tech"];
  return [];
}
